import TagManager from 'react-gtm-module'
import { GTM_ID, isGtmContainerConfigured } from './gtmConstants'

/**
 * Initialize the GTM container once at app startup (see main.tsx).
 */
export function initGtm(): void {
  if (!isGtmContainerConfigured()) return
  TagManager.initialize({
    gtmId: GTM_ID,
  })
}

function ensureDataLayer(): unknown[] | null {
  if (typeof window === 'undefined') return null
  window.dataLayer = window.dataLayer ?? []
  return window.dataLayer
}

/**
 * Low-level push for custom GTM triggers (any shape your tags expect).
 */
export function pushGtmDataLayer(payload: Record<string, unknown>): void {
  const dl = ensureDataLayer()
  if (!dl) return
  TagManager.dataLayer({ dataLayer: payload })
}

export type GtmButtonClickParams = {
  button_id: string
  label?: string
  section?: string
  destination?: string
  content_id?: string
  content_title?: string
  content_category?: string
} & Record<string, unknown>

/**
 * Standard CTA / button event for GTM (matches common tutorial patterns).
 * Use `button_id` consistently in GTM trigger conditions.
 */
export function trackGtmButtonClick(params: GtmButtonClickParams): void {
  const { button_id, label, section, destination, ...rest } = params
  if (!button_id) return
  pushGtmDataLayer({
    event: 'button_click',
    button_id,
    ...(label != null && { button_text: label }),
    ...(section != null && { section }),
    ...(destination != null && { destination }),
    page_path:
      typeof window !== 'undefined' ? window.location.pathname + window.location.search : '',
    ...rest,
  })
}

/**
 * SPA virtual pageviews — pair with a History Change trigger in GTM if needed.
 */
export function trackGtmPageView(pathnameAndSearch?: string): void {
  const page =
    pathnameAndSearch ??
    (typeof window !== 'undefined' ? window.location.pathname + window.location.search : '')
  pushGtmDataLayer({
    event: 'pageview',
    page,
  })
}
